<template>
  <config-base class="opc-zr" @view-details="viewDetails" show-plan-details>
    <zm-alert v-if="licenseAlertDesc" type="warning" show-icon :closable="false" class="opc-base-alert" slot="topAlert">
      <p class="opc-base-alert__message" v-html="licenseAlertDesc" @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanName + ' - ' + licenseAlertDesc + ' - impression')" />
    </zm-alert>
    <div slot="config" class="opc-zr__config" ref="config">
      <div class="opc-zr__quantity">
        <input-number-with-popover
          class="opc-zr__quantity-selector"
          controls-position="both"
          v-model="selectedNumber"
          size="large"
          :min="minHosts"
          :max="maxHosts"
          :decrease-tips="decreaseTips"
          :label="`${trackingPlanName} Select the number of users`"
          @change="updatePlanLicense" />
        <span class="opc-zr__quantity-unit">{{ $t('billing.zr.license_unit') }}</span>
      </div>
      <bought-license v-if="showBoughtLicense" :type="planType"/>
      <alert type="info" class="opc-zr__license-alert" v-if="mx_licenseAlert">
        <p class="opc-zr__license-alert-tips" v-html="mx_licenseAlert" />
      </alert>
      <alert type="warning" class="opc-zr__license-alert" v-if="showMaxLicenseTips">
        <p class="opc-zr__license-alert-tips" v-html="$t('billing.zr.max_license_alert', [maxHosts + 1])" />
      </alert>
      <config-cycle
        v-model="billingCycle"
        :billing-cycle-list="billingCycleList"
        :showMonthlyPriceFromCycle="true"
        @switchCycleAction="switchCycleAction"
        :unit="planUnitMap.room"
        :show-cycle-alert="showCycleAlert"
        :trackingPlanName="trackingPlanName"
      />
      <preview-payment class="opc-zr__preview-payment" />
    </div>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-244" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + trackingPlanName + ' plan'">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mx_plansCommon from '@/plans/mx/plan';
import mx_config from '@/plans/ZOOM_ROOMS/mixin';
import ConfigBase from '@/components/config-base';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { DEFAULT_MIN_HOST, trackingPlanTypes, planTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { planUnitMap } from '@/common/product-unit';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/ZOOM_ROOMS/dialog/plan-details';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { logger } from '@/utils/log';
import { isEmptyVal } from '@/utils';
import { PLAN_TYPES_WITH_MULTI_BASE_PLANS } from '@/plans/ROOM_CONNECTOR/common/utils';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';

const ConfigBaseComp = {
  extends: ConfigBase,
  mixins: [mx_config],
  data() {
    return {
      showLearnMore: false,
      configDescription: this.$t('billing.zr.desc'),
      unit: planUnitMap.room,
      showPlanDetails: true,
    };
  }
};

export default {
  name: 'zr-base',
  mixins: [mx_plansCommon, mx_config],
  components: {
    PreviewPayment,
    Alert,
    ConfigCycle,
    InputNumberWithPopover,
    ConfigBase: ConfigBaseComp,
    BoughtLicense,
  },
  data() {
    return {
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.ZOOM_ROOMS,
      planUnitMap,
      showCycleAlert: false,
      mx_licenseAlert: '',
      showBoughtLicense: false,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getSelectedPlansMap', 'getUserCurrentSubscriptions', 'getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans']),
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    mx_minHosts() {
      return this.depAddonMaxLicense || _.get(this.planList, [0, 'min'], DEFAULT_MIN_HOST);
    },
    minHosts() {
      return this.mx_minHosts;
    },
    maxHosts() {
      return _.get(this.planList, [0, 'max'], 49);
    },
    mx_decreaseTips() {
      if (this.zrAsBasePlan && this.depAddonMaxLicense >= this.selectedNumber) {
        const typeNames = _.chain(this.depAddonMaxLicensePlanList)
          .map((p) => this.$t(p.typeNameKey))
          .filter(Boolean)
          .join(', ')
          .value();
        return this.$t('billing.licences_limit_tips', [typeNames, this.title]);
      }

      return '';
    },
    decreaseTips() {
      return this.mx_decreaseTips;
    },
    zrAsBasePlan() {
      return this.getCurrentBasePlanInfo.planType === this.planType;
    },
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = _.get(this.planList, [0, 'billingCycleDependBasePlanTypeList'], []);

      return _.chain(this.getUserCurrentSubscriptions)
        .filter((p) => _.includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },
    depCurrentAllAddonLicensePlanList() {
      return _.filter(this.getUserCurrentSubscriptions,
        (p) => _.includes(p.licenseDependBasePlanTypeList, this.planType));
    },
    depCurrentAllAddonCyclePlanList() {
      return _.filter(this.getUserCurrentSubscriptions,
        (p) => _.includes(p.billingCycleDependBasePlanTypeList, this.planType));
    },
    depAddonMaxLicensePlanList() {
      return this.zrAsBasePlan ? _.orderBy(this.depCurrentAllAddonLicensePlanList, (p) => p.cartInfo?.selected_number, 'desc') : [];
    },
    depAddonMaxLicense() {
      return _.get(this.depAddonMaxLicensePlanList, '0.cartInfo.selected_number', 0);
    },
    depAddonMaxCyclePlan() {
      return this.zrAsBasePlan ?
        _.chain(this.depCurrentAllAddonCyclePlanList)
          .orderBy((p) => p.cycleValue, 'desc')
          .get(0)
          .value() :
        null;
    },
    disabledForBulkLicense() {
      return (cycle) => {
        logger.info("bulk-license", cycle);
        return false;
      }
    },
    canRmv() {
      return false;
    },
    mx_defLicenseAlertDesc() {
      const configPlan = plansof(this.getSelectedPlans).type(this.$context.type).get(0);
      const roomPlan = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ROOMS).get(0);
      // some plans only need at least 1 base plan e.g CRC
      const { dependBasePlanTypeList = [] } = configPlan;
      const isJustBasePlanRequired = dependBasePlanTypeList.includes(planTypes.ZOOM_ROOMS);
      if (isJustBasePlanRequired) {
        if(PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(this.$context.type)) {
          return this.$t("billing.common.consumption_equal_to_base", [
            this.$t(getPlanTypeName(this.$context.type)),
            this.$t(getPlanTypeName(roomPlan.type)),
          ])
        }
      }
      return "";
    },
    showMaxLicenseTips() {
      return this.selectedNumber >= this.maxHosts;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    updatePlanLicense: _.debounce(function(newQuantity, oldQuantity) {
      const billingCycle = this.billingCycle;

      if (billingCycle) {
        this.updateSelectPlan({
          plan_code: this.selectedPlan?.plan_code,
          cartInfo: { selected_number: newQuantity },
        });

        // event tracking
        this.$tracking.buyInteraction(
          `number of licenses`,
          `zoom rooms - ${newQuantity > oldQuantity ? 'increase' : 'decrease'} - ${newQuantity}`,
        );
      }
    }, 500),
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.selectedNumber <= this.maxHosts ? this.selectedNumber : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      this.afterSwitchCycleTracking();
    },
    viewDetails() {
      this.planDetailsDialog.confirm({
        title: this.title,
        planCategory: _.get(this.planList, [0, 'category']),
      });

      this.$tracking.buyInteraction(
        'plan details selected',
        trackingPlanTypes.ZOOM_ROOMS,
      );
    },
    afterSwitchCycleTracking() {
      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.buyInteraction(
        `zoom rooms plan`,
        this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    },
    handleContactUs4License(e) {
      const classList = [...e?.target?.classList];

      if (_.includes(classList, "opc-zr-max-license-alert")) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `zoom rooms - the maximum amount is ${this.maxHosts} licenses - contact sales`,
        );
      }
    }
  },
  created() {
    const selectedPlan = this.selectedPlan;

    if (!isEmptyVal(selectedPlan)) {
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;
    }

    this.planDetailsDialog = new ServiceConfirm(PlanDetails);
  },
  mounted() {
    this.$refs.config?.addEventListener("click", this.handleContactUs4License);
  },
  beforeDestroy() {
    this.$refs.config?.removeEventListener("click", this.handleContactUs4License);
  },
  watch: {
    showMaxLicenseTips: {
      immediate: true,
      handler(val) {
        val && this.$tracking.buyInteraction(
          'banner - product level',
          `zoom rooms - the maximum amount is ${this.maxHosts} licenses - impression`
        );
      },
    }
  }
};
</script>

<style lang="scss">
.opc-zr {
  &__config {

  }

  &__quantity {
    display: flex;
    align-items: center;
    &-unit {
      margin-left: 8px;
      color: #00053D;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */
    }
  }

  &__license-alert {
    &.zm-alert {
      margin-top: 12px;
    }

    &-tips {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */

      .opc-zr-max-license-alert {
        color: #0B5CFF;
        font-weight: 600;
        text-transform: capitalize;
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
.opc-base-alert {
  &.zm-alert {
    background: #F7F7F8;
    margin-bottom: 24px !important;

    .zm-alert__icon {
      color: #666487 !important;
    }
  }

  &__message {
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 450;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
  }
}
</style>
